import React, { useEffect, useState, useCallback, memo, useMemo, useRef } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import { apiUrl, fetchJson } from '../apiBase';

const MaterialForm = ({ onSuccess, onError, schoolId }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const studentsRequestId = useRef(0);
  const [studentNotes, setStudentNotes] = useState({});

  const normalizeClassCode = useCallback((value) => {
    return String(value ?? '')
      .replace(/\u00A0/g, ' ')
      .trim()
      .toLocaleUpperCase('tr-TR');
  }, []);

  const todayTR = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [date, setDate] = useState(todayTR);

  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [savingById, setSavingById] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const notifyError = useCallback((msg) => { onErrorRef.current && onErrorRef.current(msg); }, []);
  const notifySuccess = useCallback((msg) => { onSuccessRef.current && onSuccessRef.current(msg); }, []);

  useEffect(() => {
    const run = async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      setClasses([]);
      setSelectedClass(null);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error('classes');
        const data = await res.json();
        const items = (data || [])
          .map(c => ({
            id: c?.id,
            name: normalizeClassCode(c?.name),
          }))
          .filter(x => x?.id != null && x?.name);
        items.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(items);
        setSelectedClass(items[0] || null);
      } catch (e) {
        notifyError('Şubeler yüklenemedi');
      } finally {
        setLoadingClasses(false);
      }
    };
    run();
  }, [schoolId, notifyError, normalizeClassCode]);

  const loadStudents = useCallback(async () => {
    const requestId = ++studentsRequestId.current;
    setStudents([]);
    setStudentNotes({});
    if (!schoolId || !date || !selectedClass?.id) { setLoadingStudents(false); return; }
    setLoadingStudents(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/students-materials/${encodeURIComponent(date)}/school/${encodeURIComponent(schoolId)}?class_id=${encodeURIComponent(selectedClass.id)}`));
      if (!res.ok) throw new Error('students');
      const all = await res.json();
      if (requestId !== studentsRequestId.current) return;
      const filtered = (all || []).sort((a, b) => (
        `${a?.name || ''} ${a?.surname || ''}`.localeCompare(
          `${b?.name || ''} ${b?.surname || ''}`,
          'tr-TR',
          { sensitivity: 'base' },
        ) || Number(a?.id || 0) - Number(b?.id || 0)
      ));
      setStudents(filtered);
      const notes = {};
      filtered.forEach(s => { notes[String(s.id)] = String(s.notes || ''); });
      setStudentNotes(notes);
    } catch (e) {
      if (requestId !== studentsRequestId.current) return;
      notifyError('Öğrenciler yüklenemedi');
      setStudents([]);
      setStudentNotes({});
    } finally {
      if (requestId === studentsRequestId.current) setLoadingStudents(false);
    }
  }, [schoolId, date, selectedClass, notifyError]);

  useEffect(() => {
    loadStudents();
    return () => { studentsRequestId.current += 1; };
  }, [loadStudents]);

  const maxNoLen = useMemo(() => {
    const arr = students || [];
    return arr.reduce((m, s) => {
      const no = String((s?.student_no ?? s?.id ?? ''));
      return Math.max(m, no.length);
    }, 0);
  }, [students]);

  const handleStudentNoteChange = (studentId, value) => {
    setStudentNotes(prev => ({ ...prev, [String(studentId)]: String(value).slice(0, 500) }));
  };

  const updateLocalStatus = (studentId, status) => {
    setStudents(prev => prev.map(s => String(s.id) === String(studentId) ? { ...s, status } : s));
  };

  const callUpsertSingle = async (studentId, status) => {
    if (!schoolId || !date) return;
    const sid = String(studentId);
    const prev = (students.find(s => String(s.id) === sid) || {}).status;
    const isClear = status == null;
    updateLocalStatus(sid, isClear ? 'not_recorded' : status);
    setSavingById(prevMap => ({ ...prevMap, [sid]: true }));
    try {
      const res = isClear
        ? await fetchJson(apiUrl(`/admin/materials/${encodeURIComponent(schoolId)}/student`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ student_id: Number(studentId), date: String(date) }),
          })
        : await fetchJson(apiUrl(`/admin/materials/${encodeURIComponent(schoolId)}/student`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              student_id: Number(studentId),
              date: String(date),
              status: String(status),
              notes: (studentNotes[sid] || '').slice(0, 500),
            }),
          });
      if (!res.ok) throw new Error('save');
      notifySuccess(isClear ? 'Seçim kaldırıldı' : 'Kaydedildi');
    } catch (e) {
      updateLocalStatus(sid, prev);
      notifyError('Kaydetme başarısız');
    } finally {
      setSavingById(prevMap => ({ ...prevMap, [sid]: false }));
    }
  };

  const bulkMarkUnsetComplete = async () => {
    if (!schoolId || !date || !selectedClass?.id) return;
    setBulkSaving(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/materials/${encodeURIComponent(schoolId)}/bulk-mark-unset`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: String(date),
          status: 'complete',
          class_id: selectedClass.id,
        }),
      });
      if (!res.ok) throw new Error('bulk');
      const data = await res.json().catch(() => ({}));
      await loadStudents();
      const skippedAbsent = Number(data?.skipped_absent || 0);
      notifySuccess(skippedAbsent > 0 ? `Kayıtsızlar güncellendi. Devamsız ${skippedAbsent} öğrenci atlandı.` : 'Kayıtsızlar güncellendi');
    } catch (e) {
      notifyError('Toplu işaretleme başarısız');
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={classes}
              value={selectedClass}
              getOptionLabel={(opt) => String(opt?.name || '')}
              onChange={(_, v) => setSelectedClass(v || null)}
              loading={loadingClasses}
              renderInput={(params) => <TextField {...params} label="Şube" placeholder="Seçin" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              type="date"
              label="Tarih"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {selectedClass && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Öğrenci Materyalleri
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={loadingStudents || bulkSaving}
                onClick={bulkMarkUnsetComplete}
              >
                Kayıtsızları Tam Yap
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Not: Bu toplu işlem, seçili tarihte devamsız olarak işaretlenen öğrencileri atlar.
          </Typography>

          {loadingStudents ? (
            <List>
              {Array.from({ length: 6 }).map((_, i) => (
                <ListItem key={`sk-${i}`} divider>
                  <Skeleton variant="text" width="60%" />
                </ListItem>
              ))}
            </List>
          ) : students.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>Bu şube için öğrenci bulunamadı.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {students.map((student) => (
                <StudentRow
                  key={student.id}
                  student={student}
                  note={studentNotes[student.id] || ''}
                  onNoteChange={handleStudentNoteChange}
                  onSetStatus={callUpsertSingle}
                  disabled={!!savingById[String(student.id)]}
                  maxNoLen={maxNoLen}
                />
              ))}
            </List>
          )}
        </Paper>
      )}
    </>
  );
};

const StudentRow = memo(function StudentRow({ student, note, onNoteChange, onSetStatus, disabled, maxNoLen }) {
  const current = String(student.status || '');
  const sid = String(student.id);
  const no = String(student.student_no ?? student.id ?? '');

  return (
    <ListItem divider disableGutters sx={{
      px: student.is_absent ? 1 : { xs: 0.5, sm: 0.5 },
      py: 0.9,
      borderLeft: student.is_absent ? '4px solid' : undefined,
      borderColor: student.is_absent ? 'warning.main' : undefined,
      borderRadius: student.is_absent ? 1 : undefined,
      backgroundColor: student.is_absent
        ? (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.12)
        : undefined,
    }}>
      <Box sx={{
        width: '100%',
        display: { xs: 'flex', sm: 'grid' },
        alignItems: 'center',
        gap: { xs: 0.9, sm: 1 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        overflow: 'hidden',
        gridTemplateColumns: { sm: '240px 210px 1fr' },
      }}>
        <Box sx={{
          gridColumn: '1 / 2',
          flex: { xs: '1 1 100%', sm: '0 0 240px' },
          width: { sm: '240px' },
          minWidth: 0,
        }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '.95rem', sm: '1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'nowrap' },
              display: { xs: 'block', sm: 'block' },
              WebkitLineClamp: { xs: 'unset', sm: 'unset' },
              WebkitBoxOrient: 'unset',
            }}
          >
            {(() => {
              const widthCh = Math.max(maxNoLen || 0, String(no).length);
              const block = String(no).padEnd(widthCh, ' ');
              return (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1, whiteSpace: 'nowrap' }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      display: 'inline-block',
                      width: `${widthCh}ch`,
                      whiteSpace: 'pre',
                    }}
                  >
                    {block}
                  </Box>
                  <Box component="span">{student.name} {student.surname}</Box>
                </Box>
              );
            })()}
          </Typography>
          {student.is_absent && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Devamsız — isteğe bağlı değerlendirme"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        <ButtonGroup
          size="small"
          aria-label="durum"
          disabled={disabled}
          sx={{
            borderRadius: '999px',
            overflow: 'visible',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: (theme) => theme.palette.divider,
            gridColumn: { sm: '2 / 3' },
            justifySelf: { sm: 'start' },
            flex: { xs: '0 0 auto', sm: 'initial' },
            whiteSpace: 'nowrap',
            '& .MuiButtonGroup-grouped': {
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: .55, sm: .7 },
              py: .4,
              minWidth: { xs: 56, sm: 60 },
              borderColor: (theme) => theme.palette.divider,
            },
            '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderRightColor: 'transparent' },
            '& .MuiButtonGroup-grouped:first-of-type': { borderTopLeftRadius: 999, borderBottomLeftRadius: 999 },
            '& .MuiButtonGroup-grouped:last-of-type': { borderTopRightRadius: 999, borderBottomRightRadius: 999 },
          }}
        >
          <Button
            variant={current === 'missing' ? 'contained' : 'outlined'}
            color="warning"
            onClick={() => onSetStatus(sid, current === 'missing' ? null : 'missing')}
          >
            Eksik
          </Button>
          <Button
            variant={current === 'complete' ? 'contained' : 'outlined'}
            color="success"
            onClick={() => onSetStatus(sid, current === 'complete' ? null : 'complete')}
          >
            Tam
          </Button>
        </ButtonGroup>

        <Box sx={{
          gridColumn: { sm: '3 / 4' },
          flex: { xs: '1 1 0', sm: '1 1 auto' },
          width: { xs: 'auto', sm: 'auto' },
          minWidth: { xs: 0, sm: 160 },
          display: 'block',
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Not ekle..."
            value={note}
            onChange={(e) => onNoteChange(sid, e.target.value)}
            variant="outlined"
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.8rem',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              },
            }}
          />
        </Box>
      </Box>
    </ListItem>
  );
});

export default MaterialForm;
